import { isNativeApp } from "@/lib/native"

// =====================================================
// BOOTH — Meta (Facebook) Pixel helpers
// =====================================================
// Thin, typed wrapper around the global `fbq` installed by
// <MetaPixel> (components/analytics/meta-pixel.tsx).
//
// Every helper is a no-op when the pixel isn't loaded — no env var
// configured, running on the server, or a tracker blocker ate the
// script — so call sites never need to guard.
//
// Only STANDARD events are used. Meta's ad-set "Conversion event"
// dropdown only lists events the dataset has actually received, so
// the names below are what shows up in Ads Manager once real traffic
// fires them. Keep this list in sync with docs/META-PIXEL.md.
// =====================================================

/** Standard events BOOTH fires. Restricted on purpose — see the note above. */
export type MetaStandardEvent =
  // Someone landed on a funnel page (fired automatically on every route).
  | "PageView"
  // Account created — the primary optimization event for the owner/barber ads.
  | "CompleteRegistration"
  // Inquiry sent on a hiring post / booth listing — mid-funnel intent.
  | "Lead"
  // An owner created a shop listing — the owner ads' real outcome.
  | "SubmitApplication"
  // Searched for shops or chairs (the landing hero's search field).
  | "Search"
  // Viewed a specific listing or hiring post.
  | "ViewContent"
  // Started a paid flow (reservation / subscription checkout).
  | "InitiateCheckout"
  // Paid.
  | "Purchase"

type FbqParams = Record<string, string | number | boolean | undefined>

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue?: unknown[] }
    _fbq?: unknown
  }
}

export const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID

/**
 * True when the pixel is configured and the script has loaded in this tab.
 *
 * Always false inside the native shell. The root layout already keeps the
 * script out of the app's HTML, so this is the second lock: a tracking call
 * added somewhere new must not start firing in the app just because someone
 * imported trackMeta. Tracking there needs an App Tracking Transparency
 * prompt, and without one it is a 5.1.2(i) rejection.
 */
export function pixelReady(): boolean {
  if (isNativeApp()) return false
  return typeof window !== "undefined" && typeof window.fbq === "function"
}

/**
 * Fire a standard Meta event.
 *
 * `params` is passed through to Meta — never put PII in it (no email,
 * phone, or name). Meta's own docs treat those as advanced-matching
 * fields, which we deliberately don't send.
 */
export function trackMeta(event: MetaStandardEvent, params?: FbqParams, eventId?: string): void {
  if (!pixelReady()) return
  try {
    // The fourth argument is Meta's deduplication key. When the same
    // conversion is also posted server-side (lib/meta-capi.ts) with this exact
    // id, Meta counts it once. Omit it on one side and the signup is reported
    // twice — see docs/META-PIXEL.md.
    if (eventId) window.fbq!("track", event, params, { eventID: eventId })
    else window.fbq!("track", event, params)
  } catch {
    // Analytics must never break a user flow.
  }
}

/** PageView, called by the provider on every App Router navigation. */
export function trackMetaPageView(): void {
  trackMeta("PageView")
}
